import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { uniq } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { AssemblyScreenSection } from 'src/components/assemblyScreenSection';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { Input } from 'src/components/input';
import { InputStatus } from 'src/components/input/Input.interface';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { FilterDropDownWithBadgesContainer } from 'src/features/components/addComponentPage/AddComponentPage.styled';
import { useCreateAssembly } from 'src/hooks/api/assembly/useCreateAssembly';
import { useGetAllLayoutType } from 'src/hooks/api/layoutTypes/useGetAllLayoutTypes';
import { IStatus } from 'src/interfaces';
import { lang } from 'src/utils';
import { createAssemblyEvent } from 'src/utils/google-tag-manager';

import {
  getLayoutScreenMaxWidth,
  getScaleByModalWidth,
} from './AssemblyModal.helper';
import { AssemblyModalInterface } from './AssemblyModal.interface';
import {
  AssemblyContainer,
  AssemblyTagContainer,
  ChipContainer,
  ErrorContainer,
  NameContainer,
  Wrapper,
} from './AssemblyModal.styled';

export const ASSEMBLY_SCREENS_WIDTH = 652;

const MAX_SCREEN_HEIGHT = 120;

const DUPLICATED_ASSEMBLY = 'same unique field';

export const AssemblyModal = (props: AssemblyModalInterface) => {
  const createNewAssembly = useCreateAssembly();
  const { data: layoutTypes } = useGetAllLayoutType();
  const navigate = useNavigate();
  const [inputBoxStatus, setInputBoxStatus] = useState<InputStatus | undefined>(
    undefined,
  );
  const [error, setError] = useState('');
  const [assemblyName, setAssemblyName] = useState('');
  const [assemblyTagsAdded, setAssemblyTagsAdded] = useState<string[]>([]);

  const handleChange = (value: string) => {
    setAssemblyName(value.trim());
  };

  const handleDone = async () => {
    try {
      if (assemblyName && props.layout.id) {
        const request = {
          name: assemblyName,
          layout_id: props.layout.id,
          assembly_tag_id: uniq(assemblyTagsAdded).map(Number),
          status: IStatus.DRAFT,
        };
        const result = await createNewAssembly.mutateAsync(request);
        const layoutType = layoutTypes?.find(
          (layoutType) => layoutType.id === props?.layout?.layout_type_id,
        );
        createAssemblyEvent(
          result.id,
          assemblyName,
          layoutType?.name!,
          props.layout.name,
        );
        props.handleClose();
        navigate({
          pathname: PATHS.ASSEMBLY_EDIT(result.id),
        });
        Snackbar.success(`${assemblyName} ${lang.successfullyCreated}`);
      }
    } catch (error: any) {
      if (error.response.data.error.includes(DUPLICATED_ASSEMBLY)) {
        setInputBoxStatus(InputStatus.ERROR);
        setError(lang.duplicatedAssemblyErrorMsg);
      }
      Snackbar.error(lang.failedToCreateComponent);
    }
  };

  const scale = getScaleByModalWidth(
    props.layout.screens[0].width,
    props.layout.screens[0].height,
    getLayoutScreenMaxWidth(
      ASSEMBLY_SCREENS_WIDTH,
      props.layout.screens.length,
    ),
    MAX_SCREEN_HEIGHT,
  );

  return (
    <Wrapper>
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        Create new assembly
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={props.handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Input
          label={lang.createAssemblyInputLabel}
          value={assemblyName}
          status={inputBoxStatus}
          setInputBoxStatus={setInputBoxStatus}
          onChange={handleChange}
        />
        <ErrorContainer>{error && error}</ErrorContainer>
        <AssemblyTagContainer>
          <FilterDropDownWithBadgesContainer>
            <FilterDropdownWithSearch
              name={lang.assemblyTagText}
              label={lang.assemblyTagText}
              placeholder={lang.addAssemblyTag}
              multiline={true}
              onChange={(value) => {
                setAssemblyTagsAdded(value as any);
              }}
              value={assemblyTagsAdded}
              badgeOptions={{
                align: 'bottom',
              }}
            />
          </FilterDropDownWithBadgesContainer>
        </AssemblyTagContainer>
        <AssemblyContainer>
          <AssemblyLayout>
            {props.layout.screens.map((screen, index) => (
              <AssemblyScreen
                key={screen.name + index}
                height={screen.height}
                width={screen.width}
                scale={scale}
                selectable={false}
                type="screen">
                {screen.sections.map((section, index) => (
                  <AssemblyScreenSection
                    key={section.name + index}
                    section={section}
                    scale={scale}
                    selectable={true}
                    showEditIcon={false}
                  />
                ))}
              </AssemblyScreen>
            ))}
          </AssemblyLayout>
          <ChipContainer>
            {props.layout.config?.sectionTypes?.map((sectionType) => (
              <DynamicTooltipChip chipName={sectionType} />
            ))}
          </ChipContainer>
          <NameContainer>{`${props.layout.id} - ${props.layout.name}`}</NameContainer>
        </AssemblyContainer>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={assemblyName === '' ? true : false}
          onClick={handleDone}>
          Begin creating
        </Button>
      </DialogActions>
    </Wrapper>
  );
};
